import React, { useEffect, useRef } from "react";
import proj4 from "proj4";
import GeoJSON from "ol/format/GeoJSON";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { useMap } from "../../hooks";
import { register } from "ol/proj/proj4";
import useDrawSource from "../../hooks/incontext/useDrawSource";
import { useVectorSourceContext } from "../../hooks/incontext/useVectorSourceContext";
import { useId } from "react";
import { getGeoJsonFeatureStyle } from "../../utils/annotationStyleCache";

proj4.defs(
  "EPSG:5185",
  "+proj=tmerc +lat_0=38 +lon_0=125 +k=1 +x_0=200000 +y_0=600000 +ellps=GRS80 +units=m +no_defs"
);

proj4.defs(
  "EPSG:5186",
  "+proj=tmerc +lat_0=38 +lon_0=127 +k=1 +x_0=200000 +y_0=600000 +ellps=GRS80 +units=m +no_defs"
);

proj4.defs(
  "EPSG:5187",
  "+proj=tmerc +lat_0=38 +lon_0=129 +k=1 +x_0=200000 +y_0=600000 +ellps=GRS80 +units=m +no_defs"
);

proj4.defs(
  "EPSG:5188",
  "+proj=tmerc +lat_0=38 +lon_0=131 +k=1 +x_0=200000 +y_0=600000 +ellps=GRS80 +units=m +no_defs"
);

proj4.defs(
  "EPSG:3857",
  "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs"
);

proj4.defs("EPSG:32648", "+proj=utm +zone=48 +datum=WGS84 +units=m +no_defs");

proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");

proj4.defs("WGS:84", "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs");

register(proj4);

export type Coordinate =
  | "EPSG:5185"
  | "EPSG:4326"
  | "EPSG:5186"
  | "EPSG:5187"
  | "EPSG:5188"
  | "EPSG:3857"
  | "EPSG:32648"
  | "WGS:84";

/**
 * GeoJSON 벡터 레이어 props — 일반 GeoJSON 표시용.
 *
 * `FeaturesStore` 와 동시 사용 시 중복 렌더링됨 (`FeaturesStore` 내부에서 `IssueGeoJsonLayer` 자동 렌더).
 * 피처별 스타일이 필요하면 {@link StyledGeoJsonLayer} 를 쓴다.
 */
export interface GeoJsonLayerProps {
  geoJson: Record<string, any>;

  /**
   * @default "EPSG:5186"
   * @description You can set coordinate option of geJson.
   */
  projectionCode?: Coordinate;

  color: string;
  zIndex?: number;
}

export function GeoJsonLayer({
  geoJson,
  zIndex = 1,
  color = "blue",
  projectionCode = "EPSG:5186",
}: GeoJsonLayerProps) {
  const map = useMap();
  const { drawVectorSource } = useDrawSource();
  const { registerVectorSource, unregisterVectorSource } =
    useVectorSourceContext();
  const layerId = useId();
  const geoJsonLayer = useRef<VectorLayer<VectorSource> | null>(null);

  const fromProjection = projectionCode;
  const toProjection = "EPSG:3857";

  useEffect(() => {
    if (geoJsonLayer.current) {
      geoJsonLayer.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    const geoJsonFormat = new GeoJSON({ extractGeometryName: true });
    const features = geoJsonFormat.readFeatures(geoJson);

    // 원본 GeoJSON의 id를 OpenLayers Feature로 동기화
    if (geoJson && Array.isArray((geoJson as any).features)) {
      const originalFeatures = (geoJson as any).features as any[];
      for (let i = 0; i < features.length && i < originalFeatures.length; i++) {
        const src = originalFeatures[i];
        const srcId = String(src?.id ?? src?.properties?.id ?? "");
        if (srcId) {
          // getId()가 null인 경우에도 고유 ID 부여
          try {
            features[i].setId(srcId);
          } catch (_) {}
          try {
            features[i].set("id", srcId, true);
          } catch (_) {}
        }
      }
    }

    features.forEach((feature) => {
      const geoMetry = feature.getGeometry();

      feature.setProperties({ color });

      if (geoMetry) {
        geoMetry.transform(fromProjection, toProjection);
      }
    });

    drawVectorSource.addFeatures(features);

    // 피처 추가 후 VectorSource 등록
    registerVectorSource(layerId, drawVectorSource);

    const vectorLayer = new VectorLayer({
      zIndex,
      source: drawVectorSource,
      // 동일 속성 피처는 캐시된 동일 Style 인스턴스를 재사용한다(D4).
      style: function (feature) {
        const properties = feature.getProperties();
        return getGeoJsonFeatureStyle({
          title: properties["Text"] as string | undefined,
          color: properties["color"] as string | undefined,
        });
      },
    });

    geoJsonLayer.current = vectorLayer;

    // 표시 전용 레이어이므로 Snap 인터랙션을 붙이지 않는다(D3).
    map.addLayer(vectorLayer);

    return () => {
      unregisterVectorSource(layerId);
      map.removeLayer(vectorLayer);
      features.forEach((feat) => drawVectorSource.removeFeature(feat));
    };
    // zIndex 는 전용 effect 에서 setZIndex 로 처리하므로 재빌드 의존성에서 제외한다(D5).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    geoJson,
    color,
    projectionCode,
    layerId,
    drawVectorSource,
    registerVectorSource,
    unregisterVectorSource,
    fromProjection,
  ]);
  return null;
}
